import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { isoDateStringToUSFormat } from '@/util/time'
import { TimelineEvent, UITimelineEvent, UIEventFieldItem, EventFieldItem } from './schemas'

/**
 * Select: Timeline Events List by Job ID
 *
 * Returns the list of timeline events for the given job id, or null if it does not exist.
 *
 * @returns
 * - `null` - Data has not been loaded yet (key doesn't exist in store)
 * - `[]` - Data was loaded but the job has no events (empty array)
 * - `TimelineEvent[]` - Data was loaded and contains events
 */
export const selectTimelineEventsListByJobId = createSelector(
  [(state: RootState) => state.timelineEventsState.events, (_: RootState, props: { timelineJobId: string | undefined | null }) => props.timelineJobId],
  (events, timelineJobId): TimelineEvent[] | null => {
    if (!timelineJobId) return null
    // Return null if key doesn't exist (not loaded), otherwise return the array (could be empty or have data)
    return events[timelineJobId] ?? null
  }
)

/**
 * Helper: Convert EventFieldItem to UIEventFieldItem
 */
function toUIEventFieldItem<T>(item: EventFieldItem<T>, transformValue: (value: T, item: EventFieldItem<T>) => string): UIEventFieldItem {
  return {
    reference_id: item.reference_id,
    text: transformValue(item.value, item),
  }
}

function timelineDateText(value: string | null, item: EventFieldItem<string | null>): string {
  if (item.date_parse_failed) {
    return item.raw_fact ? `${item.raw_fact} (unverified date)` : 'Unverified date'
  }
  return value ? isoDateStringToUSFormat(value) : item.raw_fact ?? 'Unverified date'
}

/**
 * Select: UI Timeline Events List by Job ID
 *
 * Returns the list of timeline events transformed to UI-ready format with all preprocessing applied:
 * - Dates formatted as strings
 * - All values converted to text strings
 *
 * @returns
 * - `null` - Data has not been loaded yet (key doesn't exist in store)
 * - `[]` - Data was loaded but the job has no events (empty array)
 * - `UITimelineEvent[]` - Data was loaded and contains preprocessed events
 */
export const selectUITimelineEventsListByJobId = createSelector([selectTimelineEventsListByJobId], (eventsList): UITimelineEvent[] | null => {
  if (eventsList === null) return null

  return eventsList.map((event): UITimelineEvent => {
    return {
      id: event.id,
      eventDate: toUIEventFieldItem(event.event_date, timelineDateText),
      description: event.description.map((item) => toUIEventFieldItem(item, (value) => value)),
      source: event.source,
      pages: event.pages,
    }
  })
})
